import type { Look } from '../../shared/appearance';

/** The subset of Phaser.GameObjects.Graphics a figure needs; an SVG pen implements it for the picker preview. */
export interface Pen {
  fillStyle(color: number, alpha?: number): unknown;
  lineStyle(width: number, color: number, alpha?: number): unknown;
  fillEllipse(x: number, y: number, width: number, height: number): unknown;
  fillCircle(x: number, y: number, radius: number): unknown;
  fillRoundedRect(x: number, y: number, width: number, height: number, radius: number): unknown;
  fillTriangle(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): unknown;
  lineBetween(x1: number, y1: number, x2: number, y2: number): unknown;
  strokeEllipse(x: number, y: number, width: number, height: number): unknown;
  save(): unknown;
  restore(): unknown;
  translateCanvas(x: number, y: number): unknown;
  scaleCanvas(x: number, y: number): unknown;
}

export type Pose = {
  action: string;
  /** 0–1 through prostration or bow; walking cycle otherwise. */
  phase: number;
  walking: boolean;
  seated: boolean;
  facing: 'front' | 'back';
  own: boolean;
  reducedMotion: boolean;
};

/** Natural tones from light to deep, named only by number ("Tông 1–6"). */
export const SKIN_COLORS = [0xf2d6bd, 0xe2b892, 0xcc9c72, 0xad7a52, 0x82573a, 0x5a3a27] as const;
export const HAIR_COLOR_VALUES = { black: 0x26211e, darkbrown: 0x3e2d23, brown: 0x6b4a33, salt: 0x8f8b84, silver: 0xd8d4cc } as const;
/** Lay clothing in muted earth tones (AGENTS.md §3.3). No saffron or monastic brown. */
export const OUTFIT_COLORS = {
  trang: { top: 0x8ea1b3, bottom: 0x8ea1b3 },
  baba: { top: 0xc8b48e, bottom: 0x4f4c43 },
  somi: { top: 0xe3dccb, bottom: 0x6a6455 },
  len: { top: 0x95a898, bottom: 0x5a5548 },
  aodai: { top: 0xb99a93, bottom: 0xebe4d6 },
} as const;
export const CANE_COLOR = 0x6d5a44;
export const SCARF_COLOR = 0xb98c7b;

/** Slightly larger than the pre-v1 guest so tone, hair and clothing read at a glance. */
export const FIGURE_SCALE = 1.12;
const AGE_SCALE = { child: 0.74, young: 1, middle: 1, elder: 0.97 } as const;
const HEAD_SCALE = { child: 1.16, young: 1, middle: 1, elder: 1 } as const;
const BUILD_WIDTH = { slim: 0.86, mid: 1, full: 1.16 } as const;

export function figureHeight(look: Look) { return 58 * FIGURE_SCALE * AGE_SCALE[look.age]; }

export function drawFigure(g: Pen, look: Look, pose: Pose) {
  const w = BUILD_WIDTH[look.build], s = FIGURE_SCALE * AGE_SCALE[look.age], hs = HEAD_SCALE[look.age];
  const { top, bottom } = OUTFIT_COLORS[look.outfit];
  const skin = SKIN_COLORS[look.skin] ?? SKIN_COLORS[1];
  const hair = HAIR_COLOR_VALUES[look.hairColor];
  const eye = look.skin >= 4 ? 0x24190f : 0x5b4a3c;
  const { action, phase, walking, seated, facing } = pose;
  // Shadow and the own-seat ring stay one size so the ring always frames the cushion.
  g.fillStyle(0x354b33, 0.14); g.fillEllipse(0, 2, 31 * w * s / FIGURE_SCALE, 11);
  if (pose.own) { g.lineStyle(1.8, 0x567c60, 0.6); g.strokeEllipse(0, 1, 42, 17); }
  g.save(); g.scaleCanvas(s, s);

  const prostrating = action === 'prostrate' && phase > 0.3 && phase < 0.72;
  const kneeling = action === 'prostrate' && phase > 0.17 && phase < 0.85;
  if (prostrating) {
    // Five points to the ground, toward the statue: forehead, both hands, both knees.
    g.fillStyle(top); g.fillEllipse(0, -13, 28 * w, 32);
    g.fillStyle(skin); g.fillCircle(-10, -32, 4); g.fillCircle(10, -32, 4); g.fillCircle(0, -32, 8 * hs);
    g.fillStyle(hair); g.fillEllipse(0, -33, 15 * hs, 12 * hs);
    if (look.hair === 'high' || look.hair === 'low') g.fillCircle(0, -27, 3.4);
    if (look.hair === 'tail' || look.hair === 'long') g.fillRoundedRect(-3, -29, 6, 7, 3);
    g.fillStyle(look.outfit === 'trang' ? top : bottom); g.fillEllipse(-8, 0, 12, 10); g.fillEllipse(8, 0, 12, 10);
    g.restore();
    return;
  }

  const low = seated || kneeling;
  const bob = walking && !pose.reducedMotion ? Math.sin(phase * Math.PI * 2) * 1.5 : 0;
  const headY = low ? -32 : -43 + bob + (look.age === 'elder' ? 1.5 : 0);
  if (low) {
    // Crossed legs on the cushion; no shoes are drawn while seated (§10.2).
    g.fillStyle(look.outfit === 'trang' || look.outfit === 'aodai' ? top : bottom); g.fillEllipse(-8 * w, -3, 23 * w, 14); g.fillEllipse(8 * w, -3, 23 * w, 14);
  } else {
    const step = walking && !pose.reducedMotion ? Math.sin(phase * Math.PI * 2) * 3 : 0;
    const legTop = look.outfit === 'trang' ? -10 : -20;
    g.fillStyle(bottom); g.fillRoundedRect(-9 * w, legTop + step, 7 * w, 13 - legTop - 10, 3); g.fillRoundedRect(3 * w, legTop - step, 7 * w, 13 - legTop - 10, 3);
    if (look.cane) { g.lineStyle(2.2, CANE_COLOR, 1); g.lineBetween(15 * w, headY + 29, 17 * w, 1); }
  }
  const bowShift = action === 'bow' ? Math.sin(Math.min(1, phase) * Math.PI) * 7 : 0;
  const torso = low ? 23 : look.outfit === 'trang' ? 32 : look.outfit === 'aodai' ? 28 : 18;
  g.fillStyle(top); g.fillRoundedRect(-12 * w, headY + 11, 24 * w, torso, 7);
  if (look.outfit === 'aodai' && !low) { g.lineStyle(1, 0x000000, 0.12); g.lineBetween(-5 * w, headY + 33, -5 * w, headY + 39); g.lineBetween(5 * w, headY + 33, 5 * w, headY + 39); }
  if (facing === 'front') { g.fillStyle(0xf4edda, 0.45); g.fillTriangle(-3, headY + 13, 4, headY + 13, 0, headY + 23); }
  g.lineStyle(1, 0x586658, 0.27); g.lineBetween(0, headY + 20, 0, headY + (low ? 34 : Math.min(36, 11 + torso)));
  if (look.scarf) { g.fillStyle(SCARF_COLOR); g.fillRoundedRect(-9 * w, headY + 9.5, 18 * w, 4.5, 2.2); }

  drawHead(g, look, facing, headY + bowShift, skin, hair, eye, hs);

  const palms = action === 'palms' || action === 'prostrate' || action === 'bow';
  g.lineStyle(6, top, 1);
  if (palms && facing === 'back') { g.lineBetween(-11 * w, headY + 15, -14 * w, headY + 24); g.lineBetween(11 * w, headY + 15, 14 * w, headY + 24); }
  else if (palms) { g.lineBetween(-10 * w, headY + 16, -3, headY + 25); g.lineBetween(10 * w, headY + 16, 3, headY + 25); g.fillStyle(skin); g.fillRoundedRect(-3, headY + 17, 6, 10, 2); }
  else { g.lineBetween(-10 * w, headY + 16, -9 * w, headY + 30); g.lineBetween(10 * w, headY + 16, 9 * w, headY + 30); g.fillStyle(skin); g.fillCircle(-9 * w, headY + 30, 3); g.fillCircle(9 * w, headY + 30, 3); }
  if (action === 'beads') { for (let i = 0; i < 8; i++) { const angle = i / 8 * Math.PI * 2; g.fillStyle(0x786447); g.fillCircle(9 + Math.cos(angle) * 5, headY + 31 + Math.sin(angle) * 4, 1.5); } }
  g.restore();
}

/** Every style keeps hair on the head: there is no shaved or bald option (§10 — it reads as monastic). */
function drawHead(g: Pen, look: Look, facing: 'front' | 'back', y: number, skin: number, hair: number, eye: number, hs: number) {
  g.save(); g.translateCanvas(0, y); g.scaleCanvas(hs, hs);
  if (facing === 'back') {
    // Seated guests face the statue, so the back of the head is how family recognise each other.
    g.fillStyle(skin); g.fillRoundedRect(-4, 7, 8, 7, 2);
    g.fillStyle(hair); g.fillEllipse(0, -1, 22, 24);
    if (look.hair === 'long') g.fillRoundedRect(-11, 0, 22, 16, 5);
    if (look.hair === 'tail') g.fillRoundedRect(-2.2, 8, 4.4, 10, 2.2);
    if (look.hair === 'low') g.fillCircle(0, 9.5, 4.4);
    if (look.hair === 'high') g.fillCircle(0, -13, 4.8);
    g.lineStyle(1, 0xffffff, 0.16); g.lineBetween(-6, -7, 3, -10);
  } else {
    if (look.hair === 'long') { g.fillStyle(hair); g.fillRoundedRect(-11, -4, 22, 17, 5); }
    g.fillStyle(skin); g.fillEllipse(0, 0, 20, 23);
    g.fillStyle(hair); g.fillEllipse(0, -6, 21, look.hair === 'fringe' ? 14 : 12);
    if (look.hair === 'fringe') g.fillRoundedRect(-8.5, -6.5, 17, 4.5, 2);
    if (look.hair === 'high') g.fillCircle(0, -13.5, 4.6);
    if (look.hair !== 'fringe') { g.fillCircle(-7, -1, 4); g.fillCircle(8, -1, 3); }
    g.lineStyle(1, eye, 0.8); g.lineBetween(-5, 3, -3, 3); g.lineBetween(3, 3, 5, 3);
    if (look.glasses) { g.lineStyle(0.9, 0x3a322b, 0.9); g.strokeEllipse(-4, 3, 6, 5); g.strokeEllipse(4, 3, 6, 5); g.lineBetween(-1, 3, 1, 3); }
  }
  g.restore();
}
